import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { BehaviorSubject, combineLatest, Subscription } from 'rxjs';
import { AuthService } from 'src/app/services';
import { IPlayerDetails, IUser, StripeEnvironment } from 'tolaria-cloud-functions/src/_interfaces';
import { IEventFilter } from '../../play/events/services/event-listing.service';

@Injectable({
  providedIn: 'root'
})
export class UserService {

  public documentsLoaded$ = new BehaviorSubject<boolean | null>(null)
  public playerDocument$ = new BehaviorSubject<IPlayerDetails>(null)
  public userDocument$ = new BehaviorSubject<IUser>(null)
  public eventFilter$ = new BehaviorSubject<IEventFilter>(null)
  private userSub: Subscription
  private playerSub: Subscription
  private eventFilterSub: Subscription
  private userId: string | null = null
  private environment: StripeEnvironment = window.location.hostname.includes('tolaria.app') ? 'prod' : 'test'


  constructor(
    private readonly auth: AuthService,
    private readonly firestore: AngularFirestore,
  ) {
    this.auth.isLoggedIn$.subscribe(state => {
      console.log('UserService:: auth state changed', state)
      if (state) {
        this.getDocuments()
      }
      else {
        this.clearDocuments()
      }
    })
  }

  private async getDocuments() {

    const userObserver = this.firestore.collection('users').doc<IUser>(this.auth.user.uid).valueChanges()

    if (this.userSub && this.userId !== this.auth.user.uid) {
      console.log('UserService:: User id same, no need to refresh user document subscription')
      return
    }
    
    if (this.userSub) {
      this.userSub.unsubscribe()
    }

    this.userSub = userObserver.subscribe((doc) => {
      console.log('UserService:: User document emitted a new value', doc)
      this.userDocument$.next(doc)
    })

    this.userDocument$.subscribe(doc => {
      if (doc !== null) {
        const playerObserver = this.firestore.collection('players').doc<IPlayerDetails>(doc.playerId).valueChanges()
        this.playerSub = playerObserver.subscribe((doc) => this.playerDocument$.next(doc))
      }
      else {
        if (this.playerSub !== undefined) {
          this.playerSub.unsubscribe()
        }
      }
    })

    this.playerDocument$.subscribe(doc => {
      if (doc !== null) {
        const docObserver = this.firestore.collection('eventFilters').doc<IEventFilter>(doc.docId).valueChanges()
        this.eventFilterSub = docObserver.subscribe((doc) => this.eventFilter$.next(doc))
      }
      else {
        if (this.eventFilterSub !== undefined) {
          this.eventFilterSub.unsubscribe()
        }
      }
    })

    combineLatest([this.userDocument$, this.playerDocument$]).subscribe(([userDoc, playerDoc]) => {
      if (userDoc !== null && playerDoc !== null && this.documentsLoaded$.value === null) {
        this.documentsLoaded$.next(true)
      }
    })

    // const userSnap = await firstValueFrom(this.firestore.collection('users').doc<IUser>(this.auth.user.uid).get())
    // if (!userSnap.exists) {
    //   console.error('User document not found')
    //   return
    // }

    // const userDoc = userSnap.data() as IUser
    // console.log('UserService:: User document loaded', userDoc)
    // this.userDocument$.next(userDoc)

    // const playerSnap = await firstValueFrom(this.firestore.collection('players').doc<IPlayerDetails>(userDoc.playerId).get())
    // if (!playerSnap.exists) {
    //   console.error('Player document not found')
    //   return
    // }

    // const playerDoc = playerSnap.data() as IPlayerDetails
    // console.log('UserService:: Player document loaded', playerDoc)
    // this.playerDocument$.next(playerDoc)

  }

  private clearDocuments(): void {
    this.userId = null
    this.playerDocument$.next(null)
    this.userDocument$.next(null)
    if (this.userSub) {
      this.userSub.unsubscribe()
    }
    if (this.playerSub) {
      this.playerSub.unsubscribe()
    }
    this.documentsLoaded$.next(null)
  }

  public get isOrganizer(): boolean {
    const doc = this.userDocument$.getValue()
    if (doc === null) {
      return false
    }
    return doc.role === 'organizer'
  }

  public get isAdmin(): boolean {
    const doc = this.userDocument$.getValue()
    if (doc === null) {
      return false
    }
    return doc.role === 'admin'
  }

  public getUid(): string {
    const doc = this.userDocument$.getValue()
    if (doc === null) {
      return null
    }
    return doc.uid
  }

  public getPlayerId(): string {
    const doc = this.playerDocument$.getValue()
    if (doc === null) {
      return null
    }
    return doc.docId
  }

  public get isHero(): boolean {
    const doc = this.playerDocument$.getValue()
    if (doc === null) {
      return false
    }

    if (doc.tolariaSupportUntil > (Date.now() / 1000)) {
      return true
    }

    if (doc.identifiers && doc.identifiers.isHero) {
      return doc.identifiers.isHero
    }

    return false

  }

  public get connectedAccountId(): string | null {
    const doc = this.userDocument$.getValue()
    if (doc === null) {
      return null
    }
    return this.environment === 'prod'
      ? doc?.stripe?.id ?? null
      : doc?.stripe?.test?.id ?? null
  }

  public get defaultCurrency(): string | null {
    const doc = this.userDocument$.getValue()
    if (doc === null) {
      return null
    }
    return this.environment === 'prod'
      ? doc?.stripe?.default_currency?.toUpperCase() ?? null
      : doc?.stripe?.test?.default_currency?.toUpperCase() ?? null
  }

  public get stripeAccountId(): string | null {
    return this.environment === 'prod'
      ? this.playerDocument$.getValue()?.stripeCustomerId ?? null
      : this.playerDocument$.getValue()?.stripeTestCustomerId ?? null
  }

  public get marketplaceAdmin(): boolean {
    return this.stripeAccountId !== null
  }


}
